import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChartjsComponent } from '@coreui/angular-chartjs';
import { ChartData, ChartOptions } from 'chart.js';
import { ApiShopService } from '../../../../services/shop/api/api-shop.service';
import { AlertsService } from '../../../../services/alerts.service';
import { ShopStatisticsResultsResponse } from '../../../../models/shop/responses/api-shop-statistics-response.model';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_SECONDS = 86400;
const SEVEN_DAYS = 7;

const PALETTE = [
  'rgba(50, 110, 200, 1)',
  'rgba(40, 167, 69, 1)',
  'rgba(220, 53, 69, 1)',
  'rgba(255, 159, 64, 1)',
  'rgba(153, 102, 255, 1)',
  'rgba(23, 162, 184, 1)',
  'rgba(255, 205, 86, 1)',
  'rgba(201, 203, 207, 1)',
  'rgba(255, 99, 132, 1)',
  'rgba(75, 192, 192, 1)'
];

@Component({
  selector: 'app-shop-statistics-tab',
  templateUrl: './shop-statistics-tab.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, ChartjsComponent]
})
export class ShopStatisticsTabComponent {

  isLoading: boolean = false;
  isLoaded: boolean = false;

  startDayFilter: string = '';
  endDayFilter: string = '';

  timestamps: number[] = [];

  totalPriceAverage: number = 0;
  totalPriceMax: number = 0;
  totalPriceMin: number = 0;

  pricePercentDaySeven: number = 0;
  pricePercentDayThirty: number = 0;
  priceTotalCurrentMonth: number = 0;

  totalPriceChartData: ChartData = { labels: [], datasets: [] };
  packagesPriceChartData: ChartData = { labels: [], datasets: [] };
  packagesCountChartData: ChartData = { labels: [], datasets: [] };

  private readonly buildAxisOptions = (yTicksCallback?: (v: number | string) => string): ChartOptions => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 60, bottom: 40 } },
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          title: (items) => {
            const t = this.timestamps[items[0].dataIndex];
            if (t === undefined) return '';
            const d = new Date(t * 1000);
            return `${DAY_LABELS[d.getUTCDay()]} ${d.getUTCDate().toString().padStart(2, '0')}/${(d.getUTCMonth() + 1).toString().padStart(2, '0')}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          callback: (_value, index) => {
            const t = this.timestamps[index];
            if (t === undefined) return '';
            const d = new Date(t * 1000);
            return `${d.getUTCDate().toString().padStart(2, '0')}/${(d.getUTCMonth() + 1).toString().padStart(2, '0')}/${d.getUTCFullYear().toString().slice(-2)}`;
          }
        },
        grid: { color: 'rgba(0,0,0,0.05)' }
      },
      y: {
        beginAtZero: true,
        ticks: yTicksCallback ? { callback: (value) => yTicksCallback(value as number) } : { precision: 0 }
      }
    },
    elements: {
      line: { tension: 0.3 },
      point: { radius: 0, hitRadius: 10, hoverRadius: 4 }
    }
  });

  totalPriceChartOptions: ChartOptions = this.buildAxisOptions();
  packagesPriceChartOptions: ChartOptions = this.buildAxisOptions();
  packagesCountChartOptions: ChartOptions = this.buildAxisOptions();

  constructor(
    private apiShopService: ApiShopService,
    private alertsService: AlertsService
  ) {}

  load(): void {
    if (this.isLoaded || this.isLoading) return;
    this.refresh();
  }

  refresh(): void {
    this.isLoading = true;

    const startDayTimestamp = this.startDayFilter ? Math.floor(new Date(this.startDayFilter).getTime() / 1000) : 0;
    const endDayTimestamp = this.endDayFilter ? Math.floor(new Date(this.endDayFilter).getTime() / 1000) : 0;

    this.apiShopService.getShopStatistics(startDayTimestamp, endDayTimestamp).then(
      (response) => {
        this.isLoading = false;
        this.isLoaded = true;
        this.buildCharts(response, startDayTimestamp, endDayTimestamp);
      },
      error => {
        this.isLoading = false;
        this.alertsService.displayAlertError('Error loading shop statistics : ' + error);
      }
    );
  }

  onClickResetFilters(): void {
    this.startDayFilter = '';
    this.endDayFilter = '';
    this.refresh();
  }

  private buildCharts(response: ShopStatisticsResultsResponse, filterStartTimestamp: number, filterEndTimestamp: number): void {
    this.pricePercentDaySeven = response.priceAmountPercentDaySeven;
    this.pricePercentDayThirty = response.priceAmountPercentDayThirty;
    this.priceTotalCurrentMonth = response.priceAmountTotalCurrentMonth;

    const byDay = new Map<number, { total: number; packages: Map<string, { priceAmount: number; count: number }> }>();
    const packageIds = new Set<string>();

    for (const s of response.results) {
      const dayStart = Math.floor(s.dayTimestamp / DAY_SECONDS) * DAY_SECONDS;
      const packages = new Map<string, { priceAmount: number; count: number }>();
      for (const p of s.shopPackages) {
        packages.set(p.packageId, { priceAmount: p.priceAmountTotal, count: p.count });
        packageIds.add(p.packageId);
      }
      byDay.set(dayStart, { total: s.priceAmountTotal, packages });
    }

    let startDay: number;
    let endDay: number;
    if (filterStartTimestamp > 0 && filterEndTimestamp > 0) {
      startDay = Math.floor(filterStartTimestamp / DAY_SECONDS) * DAY_SECONDS;
      endDay = Math.floor(filterEndTimestamp / DAY_SECONDS) * DAY_SECONDS;
    } else {
      endDay = Math.floor(Date.now() / 1000 / DAY_SECONDS) * DAY_SECONDS;
      startDay = endDay - (SEVEN_DAYS - 1) * DAY_SECONDS;
    }

    const timestamps: number[] = [];
    const totalPrices: number[] = [];
    const packagePrices: { [packageId: string]: number[] } = {};
    const packageCounts: { [packageId: string]: number[] } = {};

    const sortedPackageIds = Array.from(packageIds).sort();
    for (const id of sortedPackageIds) {
      packagePrices[id] = [];
      packageCounts[id] = [];
    }

    for (let t = startDay; t <= endDay; t += DAY_SECONDS) {
      timestamps.push(t);
      const entry = byDay.get(t);
      totalPrices.push(entry?.total ?? 0);
      for (const id of sortedPackageIds) {
        const pkg = entry?.packages.get(id);
        packagePrices[id].push(pkg?.priceAmount ?? 0);
        packageCounts[id].push(pkg?.count ?? 0);
      }
    }

    this.timestamps = timestamps;

    if (totalPrices.length > 0) {
      this.totalPriceAverage = Math.round(totalPrices.reduce((sum, v) => sum + v, 0) / totalPrices.length);
      this.totalPriceMax = Math.max(...totalPrices);
      this.totalPriceMin = Math.min(...totalPrices);
    } else {
      this.totalPriceAverage = 0;
      this.totalPriceMax = 0;
      this.totalPriceMin = 0;
    }

    const labels = timestamps.map((_, i) => i.toString());

    this.totalPriceChartData = {
      labels,
      datasets: [{
        label: 'Price total',
        data: totalPrices,
        backgroundColor: 'rgba(50, 110, 200, 0.15)',
        borderColor: 'rgba(50, 110, 200, 1)',
        pointHoverBackgroundColor: 'rgba(50, 110, 200, 1)',
        borderWidth: 2,
        fill: true
      }]
    };

    this.packagesPriceChartData = {
      labels,
      datasets: sortedPackageIds.map((id, i) => {
        const color = PALETTE[i % PALETTE.length];
        return {
          label: id,
          data: packagePrices[id],
          backgroundColor: color.replace(', 1)', ', 0.15)'),
          borderColor: color,
          pointHoverBackgroundColor: color,
          borderWidth: 2,
          fill: false
        };
      })
    };

    this.packagesCountChartData = {
      labels,
      datasets: sortedPackageIds.map((id, i) => {
        const color = PALETTE[i % PALETTE.length];
        return {
          label: id,
          data: packageCounts[id],
          backgroundColor: color.replace(', 1)', ', 0.15)'),
          borderColor: color,
          pointHoverBackgroundColor: color,
          borderWidth: 2,
          fill: false
        };
      })
    };
  }
}
